import {
  useFetchRestaurantEventDisclaimers,
  useRestaurantEventDisclaimerValidation,
} from 'webReservations/checkout/hooks';
import { useRestaurantContext } from 'webReservations/context/RestaurantContext';
import { useUserContext } from 'webReservations/context/UserContext';
import { Layout } from 'webReservations/layout/Layout';
import { EVENT_PAGE_PATH } from 'webReservations/paths';
import { useGuestLandingPaths } from 'webReservations/hooks/useGuestLandingPaths';
import { CheckoutReceiptDetails } from '@components/checkout/CheckoutReceiptDetails';
import { LoadingPage } from '@components/LoadingPage';
import { NewStripeElements } from '@components/stripe/StripeElements';
import cx from 'classnames';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import typography from '~styles/typography.scss';
import { GUEST_COUNT_SEARCH_PARAM } from './constants';
import { EventCheckoutForm } from './EventCheckoutForm';
import styles from './EventCheckoutPage.scss';
import { EventDisclaimers } from './EventDisclaimers';
import { useRestaurantEvent, useRestaurantEventPricing } from './hooks';
import { RestaurantEventDetailsPageHeader } from './RestaurantEventPageHeader';
import { RestaurantEventCheckout } from './RestaurantEventCheckout';

export const EventCheckoutPage = () => {
  const { restaurantDetails: restaurant } = useRestaurantContext();
  const { isAuthenticated } = useUserContext();
  const { locationSlug, nameSlug, eventId } = useParams();
  const [searchParams] = useSearchParams();
  const { legacyRestaurantBasePath } = useGuestLandingPaths();

  const guestCount = Number(searchParams.get(GUEST_COUNT_SEARCH_PARAM));

  const { data: event, isLoading: isEventLoading } = useRestaurantEvent();

  const { data: eventPricing, isLoading: isEventTiersLoading } =
    useRestaurantEventPricing();

  const isLoading = isEventLoading || isEventTiersLoading;

  const { data: disclaimers } = useFetchRestaurantEventDisclaimers(eventId);
  const { isDisclaimerChecked, handleDisclaimerToggle, allDisclaimersChecked } =
    useRestaurantEventDisclaimerValidation(eventId);

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`${legacyRestaurantBasePath({
          locationSlug: locationSlug as string,
          nameSlug: nameSlug as string,
        })}/${EVENT_PAGE_PATH}/${eventId}`}
        replace
      />
    );
  }

  if (isLoading || !event || !eventPricing) {
    return <LoadingPage />;
  }

  const { transactionLineItems, tax, fee, total, subtotal } = eventPricing;

  return (
    <Layout hideBorder>
      <RestaurantEventDetailsPageHeader event={event} restaurant={restaurant} />
      <div className={cx(styles.pageMargin, styles.pageContent)}>
        <main className={styles.container}>
          <RestaurantEventCheckout
            restaurantEvent={event}
            date={event.shift.startDate}
            time={event.shift.startTime}
            guestCount={guestCount}
          />
          <section>
            <NewStripeElements amount={total}>
              <h3 className={typography.h6}>Check out</h3>
              <CheckoutReceiptDetails
                fee={fee}
                hideZeroFee
                lineItems={transactionLineItems}
                tax={tax}
                total={total}
              />
              <EventDisclaimers
                disclaimers={disclaimers}
                isDisclaimerChecked={isDisclaimerChecked}
                handleDisclaimerToggle={handleDisclaimerToggle}
              />
              <EventCheckoutForm
                disabled={!allDisclaimersChecked}
                guestCount={guestCount}
                transactionLineItems={transactionLineItems}
                tax={tax}
                fee={fee}
                total={total}
                subtotal={subtotal}
              />
            </NewStripeElements>
          </section>
        </main>
      </div>
    </Layout>
  );
};
